import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { DataAPIContext } from '../api';
import {
    fetchCampaignNotSent, fetchCampaignRecipients, fetchCampaignReport, fetchCampaignUndelivered,
} from './campaignStorage';

const useStyles = makeStyles((theme) => ({
    pageRoot: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    headerTitleBlock: {
        display: 'flex',
        flexDirection: 'column',
    },
    headerControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    // ---- KPI cards row ----
    kpiRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: theme.spacing(2),
        position: 'relative',
    },
    kpiCardContent: {
        padding: theme.spacing(2, 2.5),
        '&:last-child': { paddingBottom: theme.spacing(2) },
    },
    kpiHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
    },
    kpiLabelGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    kpiIconWrapper: {
        width: 32,
        height: 32,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.action.hover,
    },
    kpiValue: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.1,
    },
    kpiCaption: {
        fontSize: '0.78rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    kpiProgress: {
        height: 4,
        borderRadius: 4,
        marginTop: theme.spacing(1.5),
    },
    // ---- Chart row ----
    chartRow: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
        },
    },
    chartCardHeader: {
        textTransform: 'uppercase',
        letterSpacing: 1,
        fontSize: '0.72rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    chartLegend: {
        display: 'flex',
        gap: theme.spacing(2),
        fontSize: '0.78rem',
    },
    chartLegendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        color: theme.palette.text.secondary,
    },
    chartLegendSwatch: {
        width: 10,
        height: 10,
        borderRadius: 2,
    },
    donutLegendRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0.5, 0),
        fontSize: '0.86rem',
    },
    // ---- Recipients table ----
    recipientsHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
        flexWrap: 'wrap',
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.25, 1),
        borderRadius: 999,
        fontSize: '0.74rem',
        fontWeight: 500,
        lineHeight: 1.4,
    },
}));

// ---------------------------------------------------------------------------
// Date-range chips (replace the old From/To inputs). Match the designer
// mock's "7 days / 30 days / All time" pattern; custom-range opens a modal
// with two date pickers.
// ---------------------------------------------------------------------------

const RANGE_PRESETS = {
    '7d': { label: '7 days', days: 7 },
    '30d': { label: '30 days', days: 30 },
    'all': { label: 'All time', days: null },
};

function rangeToISO(days) {
    if (!days) return { fromDate: '', toDate: '' };
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - days + 1);
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { fromDate: iso(from), toDate: iso(now) };
}

function DateRangeControls({ value, onChange }) {
    const [customOpen, setCustomOpen] = useState(false);
    const [customFrom, setCustomFrom] = useState(value.fromDate || '');
    const [customTo, setCustomTo] = useState(value.toDate || '');
    const handlePreset = (_, next) => {
        if (next === null) return; // ToggleButtonGroup fires null on deselect
        onChange({ preset: next, ...rangeToISO(RANGE_PRESETS[next]?.days) });
    };
    const handleCustomApply = () => {
        onChange({ preset: 'custom', fromDate: customFrom, toDate: customTo });
        setCustomOpen(false);
    };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={value.preset || 'all'}
                onChange={handlePreset}
                sx={{
                    '& .MuiToggleButton-root': { textTransform: 'none', px: 2 },
                }}
            >
                {Object.entries(RANGE_PRESETS).map(([key, { label }]) => (
                    <ToggleButton key={key} value={key}>{label}</ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Button
                size="small"
                variant="outlined"
                startIcon={<DateRangeIcon />}
                onClick={() => setCustomOpen(true)}
                sx={{ textTransform: 'none' }}
            >
                Custom range
            </Button>
            <Dialog open={customOpen} onClose={() => setCustomOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Custom date range</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="From"
                            type="date"
                            value={customFrom}
                            onChange={(e) => setCustomFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="To"
                            type="date"
                            value={customTo}
                            onChange={(e) => setCustomTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustomOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCustomApply}
                        disabled={!customFrom || !customTo}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// KPI card — icon + label + value + caption + progress bar + percentage chip
// ---------------------------------------------------------------------------

function KpiCard({ icon, label, value, caption, percentage, progressColor, tooltip, segments }) {
    const classes = useStyles();
    const pctLabel = percentage == null ? null : `${Number(percentage).toFixed(1)}%`;
    return (
        <Card variant="outlined">
            <CardContent className={classes.kpiCardContent}>
                <div className={classes.kpiHeaderRow}>
                    <div className={classes.kpiLabelGroup}>
                        <div className={classes.kpiIconWrapper}>{icon}</div>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {label}
                        </Typography>
                        {tooltip && (
                            <Tooltip title={tooltip} arrow placement="top">
                                <InfoOutlinedIcon
                                    sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }}
                                />
                            </Tooltip>
                        )}
                    </div>
                    {pctLabel != null && (
                        <Typography variant="body2" fontWeight={600} sx={{ color: progressColor || 'primary.main' }}>
                            {pctLabel}
                        </Typography>
                    )}
                </div>
                <Typography className={classes.kpiValue}>
                    {typeof value === 'string' ? value : Number(value || 0).toLocaleString()}
                </Typography>
                {caption && (
                    <Typography component="div" className={classes.kpiCaption}>{caption}</Typography>
                )}
                {segments ? (
                    <Box
                        className={classes.kpiProgress}
                        role="img"
                        aria-label={segments.map((s) => s.label).join(', ')}
                        sx={{ display: 'flex', overflow: 'hidden', backgroundColor: 'action.hover' }}
                    >
                        {segments.map((s) => (
                            <Box key={s.label} sx={{ width: `${s.pct}%`, backgroundColor: s.color }} />
                        ))}
                    </Box>
                ) : (
                    <LinearProgress
                        variant="determinate"
                        value={percentage == null ? 100 : Math.min(100, Math.max(0, Number(percentage)))}
                        className={classes.kpiProgress}
                        sx={{
                            backgroundColor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: progressColor || '#3D3935',
                            },
                        }}
                    />
                )}
            </CardContent>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Channel breakdown donut — total in center, legend below with counts + %
// ---------------------------------------------------------------------------

// Channel colors deliberately avoid ``PERFORMANCE_COLORS.opened`` (#4F8DFF)
// — Email used to share that blue, which made the donut and the Performance
// chart's "Opened" bars look like the same signal at a glance. Cyan-teal
// for Email keeps the "email = blue-ish" mental model without colliding.
const CHANNEL_META = [
    { key: 'email', label: 'Email', icon: MailOutlineIcon, color: '#06B6D4' },
    { key: 'chat', label: 'In-app chat', icon: ChatBubbleOutlineIcon, color: '#7B68EE' },
    { key: 'push', label: 'Push', icon: NotificationsActiveOutlinedIcon, color: '#F59E0B' },
    { key: 'sms', label: 'SMS', icon: PhoneAndroidOutlinedIcon, color: '#10B981' },
    { key: 'whatsapp', label: 'WhatsApp', icon: ChatBubbleOutlineIcon, color: '#25D366' },
    { key: 'airbnb', label: 'Airbnb', icon: ChatBubbleOutlineIcon, color: '#FF5A5F' },
];

function channelDataFromReport(report) {
    return CHANNEL_META.map((meta) => ({
        ...meta,
        count: report[`billing_${meta.key}_count`] || 0,
    })).filter((row) => row.count > 0);
}

function ChannelBreakdownCard({ report, totalSent }) {
    const classes = useStyles();
    const data = channelDataFromReport(report);
    if (data.length === 0) {
        return (
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                    <Typography className={classes.chartCardHeader}>Channel breakdown</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                        <Typography color="text.secondary" variant="body2">
                            No sends yet.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography className={classes.chartCardHeader}>Channel breakdown</Typography>
                <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="count"
                                nameKey="label"
                                cx="50%"
                                cy="50%"
                                innerRadius={62}
                                outerRadius={90}
                                paddingAngle={1}
                                stroke="none"
                            >
                                {data.map((entry) => (
                                    <Cell key={entry.key} fill={entry.color} />
                                ))}
                            </Pie>
                            <RechartsTooltip
                                formatter={(value, name) => [`${Number(value).toLocaleString()}`, name]}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1 }}>
                            {Number(totalSent).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">total sent</Typography>
                    </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                    {data.map((row) => {
                        const Icon = row.icon;
                        const share = totalSent > 0 ? (row.count / totalSent) * 100 : 0;
                        return (
                            <div key={row.key} className={classes.donutLegendRow}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            background: row.color,
                                        }}
                                    />
                                    <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2">{row.label}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {Number(row.count).toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'right' }}>
                                        {share.toFixed(1)}%
                                    </Typography>
                                </Box>
                            </div>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Performance over time — bar chart: Sent (gray) + Opened (blue) per day
// ---------------------------------------------------------------------------

const PERFORMANCE_COLORS = {
    sent: '#D5D7DA',
    opened: '#4F8DFF',
};

function PerformanceOverTimeCard({ report }) {
    const classes = useStyles();
    // Aggregate per-day across all message event types for "Sent".
    // "Opened" derives from message_log open flags — for v1 we approximate
    // using the report's existing opened_pct against per-day sends (we
    // don't yet emit a per-day opened series from the backend). When the
    // backend grows a per-day opened breakdown, swap this in.
    const chartData = useMemo(() => {
        const byDate = {};
        for (const row of report.daily || []) {
            if (!row.event_type || !row.event_type.startsWith('message_')) continue;
            const d = row.date;
            if (!byDate[d]) byDate[d] = { date: d, sent: 0 };
            byDate[d].sent += row.count;
        }
        const openedPct = Number(report.opened_pct || 0) / 100;
        const sorted = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
        return sorted.map((row) => ({
            ...row,
            opened: Math.round(row.sent * openedPct),
            dateLabel: new Date(row.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        }));
    }, [report]);
    if (chartData.length === 0) {
        return (
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                    <Typography className={classes.chartCardHeader}>Performance over time</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                        <Typography color="text.secondary" variant="body2">
                            No daily breakdown available yet.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography className={classes.chartCardHeader}>Performance over time</Typography>
                    <Box className={classes.chartLegend}>
                        <Box className={classes.chartLegendItem}>
                            <Box className={classes.chartLegendSwatch} sx={{ background: PERFORMANCE_COLORS.sent }} />
                            Sent
                        </Box>
                        <Box className={classes.chartLegendItem}>
                            <Box className={classes.chartLegendSwatch} sx={{ background: PERFORMANCE_COLORS.opened }} />
                            Opened
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ width: '100%', height: 240, mt: 1 }}>
                    <ResponsiveContainer>
                        {/* ``maxBarSize`` caps single-bar width so a
                            single-day campaign doesn't render as a giant
                            slab filling the chart. With 1 data point and
                            no cap, Recharts spreads the bar across the
                            full plot area. ~36px keeps a small-but-
                            visible bar while leaving plenty of empty
                            space around it. */}
                        <BarChart
                            data={chartData}
                            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                            maxBarSize={36}
                            barGap={2}
                        >
                            <XAxis
                                dataKey="dateLabel"
                                tickLine={false}
                                axisLine={false}
                                tick={{ fontSize: 11, fill: '#888' }}
                            />
                            <YAxis hide />
                            <RechartsTooltip
                                cursor={{ fill: 'rgba(0,0,0,0.02)' }}
                                contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }}
                            />
                            <Bar dataKey="sent" fill={PERFORMANCE_COLORS.sent} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="opened" fill={PERFORMANCE_COLORS.opened} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Main report component
// ---------------------------------------------------------------------------

// Delivered card's bar and caption, matching the opened/failed colors used elsewhere in the report.
const DELIVERY_COLORS = {
    delivered: '#4F8DFF',
    openerOnly: '#A9C6FF',
    failed: '#D32F2F',
    awaiting: '#F0B35A',
    awaitingText: '#A15C00',
    noReceipt: '#BDBDBD',
    noReceiptText: '#6B6B6B',
};

function pct(v) {
    if (v == null) return null;
    return Number(v);
}

export default function CampaignReport({ campaignId }) {
    const classes = useStyles();
    const dataAPI = useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [range, setRange] = useState({ preset: 'all', fromDate: '', toDate: '' });

    useEffect(() => {
        if (!campaignId) return undefined;
        let cancelled = false;
        setLoading(true);
        fetchCampaignReport(dataAPI, campaignId, {
            fromDate: range.fromDate,
            toDate: range.toDate,
        })
            .then((data) => {
                if (!cancelled) setReport(data);
            })
            .catch((err) => {
                if (cancelled) return;
                const detail =
                    err?.response?.data?.detail ||
                    err?.message ||
                    'Failed to load campaign report';
                enqueueSnackbar(detail, { variant: 'error' });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [campaignId, dataAPI, enqueueSnackbar, range.fromDate, range.toDate]);

    const totalSent = useMemo(() => {
        if (!report) return 0;
        return (report.billing_chat_count || 0)
            + (report.billing_sms_count || 0)
            + (report.billing_whatsapp_count || 0)
            + (report.billing_email_count || 0)
            + (report.billing_airbnb_count || 0)
            + (report.billing_push_count || 0);
    }, [report]);

    // "Delivered" counts only channels that confirm delivery (chat, email,
    // SMS, WhatsApp); push and Airbnb can't, so they're left out of
    // ``delivery_trackable_sent``. The backend leaves it null for a campaign
    // sent before delivery tracking or with nothing trackable (and an older
    // backend omits it): Delivered = Sent, as before.
    // The % is over settled sends (delivered + failed): awaiting ones never pull
    // it down. Delivered = the message itself + "opener only" (WhatsApp guests
    // outside the 24-hour window who got the opener but not, or not yet, the
    // message). An older campaign (``delivery_estimated``) is 100% minus the
    // estimated sends to numbers without WhatsApp, with no breakdown.
    const deliveryKnown = Number(report?.delivery_trackable_sent || 0) > 0;
    const deliveryEstimated = deliveryKnown && Boolean(report?.delivery_estimated);
    const deliveryTrackable = Number(report?.delivery_trackable_sent || 0);
    const failedCount = Number(report?.delivery_failed_count || 0);
    const awaitingCount = Number(report?.delivery_awaiting_count || 0);
    const noReceiptCount = Number(report?.delivery_no_receipt_count || 0);
    const openerOnlyCount = Number(report?.delivery_opener_only_count || 0);
    // Guests a channel left out on purpose (replied STOP on WhatsApp, no WhatsApp): never sent, so outside the %.
    const notSentCount = Number(report?.not_sent_count || 0);
    const delivered = deliveryKnown ? Number(report?.delivered_count || 0) : totalSent;
    const messageCount = report?.delivery_message_count != null ? Number(report.delivery_message_count) : delivered;
    const settledCount = delivered + failedCount;
    let deliveredPct = totalSent > 0 ? 100 : 0;
    if (deliveryKnown) {
        if (report?.delivered_pct != null) deliveredPct = Number(report.delivered_pct);
        else deliveredPct = settledCount > 0 ? (delivered / settledCount) * 100 : null;
    }
    const sharePct = (n) => (n / deliveryTrackable) * 100;
    // "Opened" is only meaningful on channels that report opens (chat read,
    // email pixel, WhatsApp read receipt). ``opened_trackable_sent`` is the
    // count of those sends and is the denominator behind ``opened_pct``. SMS
    // has no read receipt, so an SMS-only campaign has 0 trackable sends and
    // we show "—" rather than a misleading 0%.
    const openedTrackableSent = Number(report?.opened_trackable_sent || 0);
    const openTracked = openedTrackableSent > 0;
    const openedCount = openTracked
        ? Math.round((Number(report?.opened_pct || 0) / 100) * openedTrackableSent)
        : 0;
    const openedPct = pct(report?.opened_pct);
    // "Replied" = distinct recipients who replied on any channel (engagement),
    // decoupled from the net-new-MAU reply counts used in billing below.
    // Its base is everyone the campaign reached: the delivered sends plus the
    // push / Airbnb sends that can't confirm delivery (replies come in on those
    // too, so dividing by delivered alone could pass 100%).
    const repliedCount = Number(report?.replied_count || 0);
    const repliedBase = deliveryKnown ? delivered + Math.max(0, totalSent - deliveryTrackable) : totalSent;
    const repliedPct = repliedBase > 0 ? Math.min(100, (repliedCount / repliedBase) * 100) : 0;

    const renderHeader = (
        <div className={classes.header}>
            <div className={classes.headerTitleBlock}>
                <Typography variant="h6" fontWeight={700}>Results</Typography>
                <Typography variant="body2" color="text.secondary">
                    How this campaign performed across all channels.
                </Typography>
            </div>
            <div className={classes.headerControls}>
                <DateRangeControls value={range} onChange={setRange} />
            </div>
        </div>
    );

    if (loading && !report) {
        return (
            <Box className={classes.pageRoot}>
                {renderHeader}
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }
    if (!report) {
        return (
            <Box className={classes.pageRoot}>
                {renderHeader}
                <Box py={4}>
                    <Typography color="text.secondary" align="center">
                        No report data available yet.
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.pageRoot}>
            {renderHeader}

            {/* KPI cards with right-chevron flow connectors between them.
                Order matches the funnel: attempted → reached carrier →
                opened by recipient → replied. */}
            <Box sx={{ position: 'relative' }}>
                <div className={classes.kpiRow}>
                    <KpiCard
                        icon={<PlayArrowOutlinedIcon fontSize="small" />}
                        label="Sent"
                        value={totalSent}
                        caption="total recipients"
                        progressColor="#3D3935"
                        percentage={100}
                        tooltip="Successful sends across all enabled channels. Failed/bounced sends are not counted."
                    />
                    {deliveryKnown ? (
                        <KpiCard
                            icon={<SendOutlinedIcon fontSize="small" />}
                            label="Delivered"
                            value={delivered}
                            caption={(
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.25, rowGap: 0.5 }}>
                                    <span>
                                        of {deliveryTrackable.toLocaleString()}{' '}
                                        {deliveryTrackable === totalSent ? 'sent' : 'trackable'}
                                    </span>
                                    {failedCount > 0 && (
                                        <Box component="span" sx={{ color: DELIVERY_COLORS.failed, fontWeight: 500 }}>
                                            {failedCount.toLocaleString()}
                                            {deliveryEstimated ? ' without WhatsApp' : ' failed'}
                                        </Box>
                                    )}
                                    {awaitingCount > 0 && (
                                        <Box component="span" sx={{ color: DELIVERY_COLORS.awaitingText, fontWeight: 500 }}>
                                            {awaitingCount.toLocaleString()} awaiting confirmation
                                        </Box>
                                    )}
                                    {noReceiptCount > 0 && (
                                        <Box component="span" sx={{ color: DELIVERY_COLORS.noReceiptText, fontWeight: 500 }}>
                                            {noReceiptCount.toLocaleString()} no receipt
                                        </Box>
                                    )}
                                    {openerOnlyCount > 0 && (
                                        <span>
                                            {messageCount.toLocaleString()} got the message,
                                            {' '}{openerOnlyCount.toLocaleString()} only the WhatsApp opener
                                        </span>
                                    )}
                                    {notSentCount > 0 && (
                                        <Box component="span" sx={{ color: DELIVERY_COLORS.noReceiptText, fontWeight: 500 }}>
                                            {notSentCount.toLocaleString()} not sent on WhatsApp
                                        </Box>
                                    )}
                                    {deliveryEstimated && (
                                        <Box component="span" sx={{ fontStyle: 'italic' }}>
                                            Estimated: sent before detailed tracking
                                        </Box>
                                    )}
                                </Box>
                            )}
                            percentage={deliveredPct}
                            progressColor={DELIVERY_COLORS.delivered}
                            segments={deliveryEstimated ? [
                                { label: `${delivered.toLocaleString()} delivered`, pct: sharePct(delivered), color: DELIVERY_COLORS.delivered },
                                { label: `${failedCount.toLocaleString()} without WhatsApp`, pct: sharePct(failedCount), color: DELIVERY_COLORS.failed },
                            ] : [
                                { label: `${messageCount.toLocaleString()} got the message`, pct: sharePct(messageCount), color: DELIVERY_COLORS.delivered },
                                { label: `${openerOnlyCount.toLocaleString()} got only the WhatsApp opener`, pct: sharePct(openerOnlyCount), color: DELIVERY_COLORS.openerOnly },
                                { label: `${failedCount.toLocaleString()} failed`, pct: sharePct(failedCount), color: DELIVERY_COLORS.failed },
                                { label: `${awaitingCount.toLocaleString()} awaiting confirmation`, pct: sharePct(awaitingCount), color: DELIVERY_COLORS.awaiting },
                                { label: `${noReceiptCount.toLocaleString()} no receipt`, pct: sharePct(noReceiptCount), color: DELIVERY_COLORS.noReceipt },
                            ].filter((seg) => seg.pct > 0)}
                            tooltip={deliveryEstimated
                                ? "This campaign was sent before delivery was tracked per message, so this is an estimate: every send counts as delivered except the WhatsApp messages to numbers that don't have WhatsApp."
                                : "Delivered out of the sends that have a result (delivered or failed); sends still awaiting confirmation don't count yet. SMS counts once the carrier confirms delivery. WhatsApp counts once the message is delivered, or, for a guest outside WhatsApp's 24-hour window, the opener sent in its place (shown as \"only the WhatsApp opener\" until the guest taps it). Email counts unless it bounced or couldn't be sent. Chat counts once posted. No receipt: the phone never reported back after 3 days. Push and Airbnb can't confirm delivery and are left out."}
                        />
                    ) : (
                        <KpiCard
                            icon={<SendOutlinedIcon fontSize="small" />}
                            label="Delivered"
                            value={delivered}
                            caption="of sent"
                            percentage={deliveredPct}
                            progressColor="#4F8DFF"
                            tooltip="Sends that the carrier/provider accepted. Per-channel delivery confirmation is still being wired up — for now Delivered equals Sent."
                        />
                    )}
                    <KpiCard
                        icon={<DraftsOutlinedIcon fontSize="small" />}
                        label="Opened"
                        value={openTracked ? openedCount : '—'}
                        caption={openTracked ? `of ${openedTrackableSent.toLocaleString()} trackable` : 'not tracked for SMS'}
                        percentage={openTracked ? openedPct : null}
                        progressColor="#7B68EE"
                        tooltip="Opens are tracked on chat (message read), email (open pixel), and WhatsApp (read receipt). SMS has no read receipt, so SMS sends are excluded from the open rate — a reply is the engagement signal for SMS."
                    />
                    <KpiCard
                        icon={<ReplyOutlinedIcon fontSize="small" />}
                        label="Replied"
                        value={repliedCount}
                        caption="of delivered"
                        percentage={repliedPct}
                        progressColor="#34C759"
                        tooltip="Distinct recipients who replied to this campaign, across every channel (chat, SMS, WhatsApp). A reply within 30 days of the send counts."
                    />
                </div>
            </Box>

            <Box className={classes.chartRow}>
                <PerformanceOverTimeCard report={report} />
                <ChannelBreakdownCard report={report} totalSent={totalSent} />
            </Box>

            {deliveryKnown && !deliveryEstimated && failedCount > 0 && (
                <UndeliveredTable campaignId={campaignId} fromDate={range.fromDate} toDate={range.toDate} />
            )}

            {notSentCount > 0 && <NotSentTable campaignId={campaignId} />}

            <RecipientsTable campaignId={campaignId} />
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Not delivered — the failed sends with the guest and the reason, in the same
// words as the guest's History Log. Loads a page at a time.
// ---------------------------------------------------------------------------

const UNDELIVERED_PAGE = 25;
const CHANNEL_LABEL = { email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };

// The two per-guest lists under the cards. Not delivered: sends that failed (red). Not sent: guests a channel left
// out on purpose -- replied STOP on WhatsApp, or no WhatsApp -- whom the operator can reach another way (gray).
const ISSUE_LISTS = {
    undelivered: {
        title: 'Not delivered',
        subtitle: "Same reasons as each guest's History Log",
        empty: 'No failed sends to list in this date range.',
        loadError: 'Could not load the not-delivered list',
        badge: { color: DELIVERY_COLORS.failed, background: 'rgba(211, 47, 47, 0.08)' },
        fetch: (dataAPI, campaignId, opts) => fetchCampaignUndelivered(dataAPI, campaignId, opts),
    },
    notSent: {
        title: 'Not sent',
        subtitle: 'Reach these guests another way',
        empty: 'Nobody was left out.',
        loadError: 'Could not load the not-sent list',
        badge: { color: DELIVERY_COLORS.noReceiptText, background: 'rgba(0, 0, 0, 0.06)' },
        fetch: (dataAPI, campaignId, { offset, limit }) => fetchCampaignNotSent(dataAPI, campaignId, { offset, limit }),
    },
};

function UndeliveredTable(props) {
    return <RecipientIssueTable kind="undelivered" {...props} />;
}

function NotSentTable(props) {
    return <RecipientIssueTable kind="notSent" {...props} />;
}

function RecipientIssueTable({ kind, campaignId, fromDate, toDate }) {
    const list = ISSUE_LISTS[kind];
    const classes = useStyles();
    const dataAPI = useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = (offset, cancelled = { current: false }) => {
        setLoading(true);
        return list.fetch(dataAPI, campaignId, { fromDate, toDate, offset, limit: UNDELIVERED_PAGE })
            .then((res) => {
                if (cancelled.current) return;
                setRows((prev) => (offset === 0 ? res.results : [...prev, ...res.results]));
                setCount(res.count || 0);
            })
            .catch((err) => {
                console.warn(`${kind} list failed`, err);
                if (!cancelled.current) enqueueSnackbar(list.loadError, { variant: 'error' });
            })
            .finally(() => {
                if (!cancelled.current) setLoading(false);
            });
    };

    useEffect(() => {
        if (!campaignId) return undefined;
        const cancelled = { current: false };
        load(0, cancelled);
        return () => { cancelled.current = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, fromDate, toDate, dataAPI]);

    const headCell = { textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 };
    return (
        <Card variant="outlined">
            <CardContent>
                <div className={classes.recipientsHeaderRow}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {list.title} ({count.toLocaleString()})
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {list.subtitle}
                    </Typography>
                </div>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCell}>Guest</TableCell>
                            <TableCell sx={headCell}>Channel</TableCell>
                            <TableCell sx={headCell}>Status and reason</TableCell>
                            <TableCell sx={headCell}>When</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r, i) => {
                            const ChannelIcon = CHANNEL_ICON_FOR_KEY[r.channel] || ChatBubbleOutlineIcon;
                            const displayName = r.recipient_name || '—';
                            const when = r.ts ? new Date(r.ts) : null;
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={`${r.recipient_id}-${r.ts}-${i}`} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: avatarColorOf(displayName),
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: '0.86rem',
                                                }}
                                            >
                                                {initialsOf(displayName)}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={500}>{displayName}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <ChannelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2">{CHANNEL_LABEL[r.channel] || r.channel}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className={classes.statusBadge}
                                            style={list.badge}
                                        >
                                            {r.label}
                                        </span>
                                        {r.reason && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                {r.reason}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {when
                                                ? when.toLocaleString(undefined, {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: 'numeric',
                                                    minute: '2-digit',
                                                })
                                                : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {r.recipient_id && (
                                            <Tooltip title="Open history log (opens in new tab)" arrow placement="left">
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={`/user/${r.recipient_id}?tab=1`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <LaunchIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && rows.length === 0 && count === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography color="text.secondary" variant="body2" textAlign="center" py={2}>
                                        {list.empty}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {rows.length < count && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Button size="small" disabled={loading} onClick={() => load(rows.length)}>
                            Show {Math.min(UNDELIVERED_PAGE, count - rows.length).toLocaleString()} more
                            {' '}of {(count - rows.length).toLocaleString()}
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Recipients table — avatar + name/email + channel icon + status badge +
// formatted timestamp + "open history log" icon (the recipient's user-profile
// History Log tab, where their chat + email logs live)
// ---------------------------------------------------------------------------

const RECIPIENTS_PER_PAGE = 25;
const CHANNEL_OPTIONS = [
    { value: '', label: 'All channels' },
    { value: 'chat', label: 'In-app chat' },
    { value: 'email', label: 'Email' },
    { value: 'airbnb', label: 'Airbnb' },
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'push', label: 'Push' },
];

const CHANNEL_ICON_FOR_KEY = {
    chat: ChatBubbleOutlineIcon,
    email: MailOutlineIcon,
    sms: PhoneAndroidOutlinedIcon,
    whatsapp: ChatBubbleOutlineIcon,
    airbnb: ChatBubbleOutlineIcon,
    push: NotificationsActiveOutlinedIcon,
};

// Status mapping is intentionally narrow for v1: we don't yet emit
// distinct per-recipient "Delivered" / "Opened" / "Replied" events, so
// every row that exists in Campaign_Attribution is rendered as "Sent".
// When the backend grows per-recipient state, expand this map.
const STATUS_FOR_RECIPIENT = () => ({
    label: 'Sent',
    color: '#1D7AFC',
    bg: 'rgba(29, 122, 252, 0.1)',
});

function initialsOf(name) {
    if (!name) return '?';
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join('');
}

function avatarColorOf(name) {
    if (!name) return '#9E9E9E';
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = (hash * 31 + name.charCodeAt(i)) | 0;
    }
    const palette = ['#E57373', '#BA68C8', '#7986CB', '#4FC3F7', '#4DB6AC', '#AED581', '#FFB74D', '#A1887F'];
    return palette[Math.abs(hash) % palette.length];
}

function RecipientsTable({ campaignId }) {
    const classes = useStyles();
    const dataAPI = useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [page, setPage] = useState(1);
    const [channel, setChannel] = useState('');
    const [data, setData] = useState({ count: 0, results: [] });
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');

    // Debounce search input: avoid an API hit on every keystroke. 250ms is
    // long enough to skip mid-word fetches but short enough to feel snappy.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 250);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Reset to page 1 whenever the filter or search changes, otherwise the
    // operator could land on page 5 of a 2-page result set.
    useEffect(() => { setPage(1); }, [channel, debouncedQ]);

    useEffect(() => {
        if (!campaignId) return undefined;
        let cancelled = false;
        setLoading(true);
        fetchCampaignRecipients(dataAPI, campaignId, {
            page,
            per_page: RECIPIENTS_PER_PAGE,
            channel: channel || null,
            q: debouncedQ || null,
        })
            .then((res) => {
                if (!cancelled) setData(res);
            })
            .catch((err) => {
                console.warn('fetchCampaignRecipients failed', err);
                if (!cancelled) {
                    enqueueSnackbar('Could not load recipients list', { variant: 'error' });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [campaignId, page, channel, debouncedQ, dataAPI, enqueueSnackbar]);

    const totalPages = data?.count ? Math.max(1, Math.ceil(data.count / RECIPIENTS_PER_PAGE)) : 1;

    return (
        <Card variant="outlined">
            <CardContent>
                <div className={classes.recipientsHeaderRow}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} component="span">
                            Recipients
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                            · {Number(data.count || 0).toLocaleString()} delivered
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <TextField
                        size="small"
                        placeholder="Search recipients…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 260 }}
                    />
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 160 }}>
                        <InputLabel>Channel</InputLabel>
                        <Select
                            value={channel}
                            label="Channel"
                            onChange={(e) => setChannel(e.target.value)}
                        >
                            {CHANNEL_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Recipient</TableCell>
                            <TableCell sx={{ textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Channel</TableCell>
                            <TableCell sx={{ textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Status</TableCell>
                            <TableCell sx={{ textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Sent at</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(data.results || []).map((r) => {
                            const status = STATUS_FOR_RECIPIENT(r);
                            const ChannelIcon = CHANNEL_ICON_FOR_KEY[r.channel] || ChatBubbleOutlineIcon;
                            const channelLabel = r.channel
                                ? r.channel.charAt(0).toUpperCase() + r.channel.slice(1)
                                : '—';
                            const displayName = r.recipient_name || '—';
                            const sentAt = r.created_ts ? new Date(r.created_ts) : null;
                            return (
                                <TableRow key={r.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: avatarColorOf(displayName),
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: '0.86rem',
                                                }}
                                            >
                                                {initialsOf(displayName)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {displayName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {r.recipient_email || ''}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <ChannelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2">{channelLabel}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className={classes.statusBadge}
                                            style={{ color: status.color, background: status.bg }}
                                        >
                                            <CheckCircleOutlineIcon sx={{ fontSize: 12 }} />
                                            {status.label}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {sentAt
                                                ? sentAt.toLocaleString(undefined, {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })
                                                : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip
                                            title={
                                                r.recipient_id
                                                    ? 'Open history log (opens in new tab)'
                                                    : 'No history log for this row'
                                            }
                                            arrow
                                            placement="left"
                                        >
                                            <span>
                                                {/* CP-8146: an anchor rather than a history.push so the
                                                    operator keeps the analytics report open while reading
                                                    the recipient's history. tab=1 is the user-profile
                                                    History Log tab where their chat + email logs live.
                                                    Push rows carry no MessageLog recipient, so they stay
                                                    a disabled button with no href. */}
                                                <IconButton
                                                    size="small"
                                                    disabled={!r.recipient_id}
                                                    {...(r.recipient_id
                                                        ? {
                                                            component: 'a',
                                                            href: `/user/${r.recipient_id}?tab=1`,
                                                            target: '_blank',
                                                            rel: 'noopener noreferrer',
                                                        }
                                                        : {})}
                                                >
                                                    <LaunchIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && (data.results || []).length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography color="text.secondary" variant="body2" textAlign="center" py={3}>
                                        {debouncedQ
                                            ? `No recipients match "${debouncedQ}"`
                                            : `No deliveries recorded ${channel ? `via ${channel}` : 'yet'}.`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        Prev
                    </Button>
                    <Typography variant="caption">Page {page} / {totalPages}</Typography>
                    <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                        Next
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}
